// @flow

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import {colors} from '../../../assets/styles/colors';
import {InfoOutlined, PlayCircleFilledTwoTone, Share} from '@material-ui/icons';
import DateRangeIcon from '@material-ui/icons/DateRange';
import {
  Hidden,
  Grid,
  Divider,
  ClickAwayListener,
  Tooltip,
  useMediaQuery,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import OrderCard from './OrderCard';
import {usePage} from '../../page/utils';

import purpleEllipsesPageViewsBackground from '../../../assets/img/dashboard/purpleEllipsesPageViewsBackground.svg';
import whiteGlobe from '../../../assets/img/dashboard/whiteGlobe.svg';
import stats1 from '../../../assets/img/dashboard/stats-1.svg';
import stats2 from '../../../assets/img/dashboard/stats-2.svg';
import stats3 from '../../../assets/img/dashboard/stats-3.svg';

import {ORDER_STATES} from '../../../entities';

import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import dayjs from 'dayjs';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';

import {commaFormat} from '../../../utils/number';
import {
  abvMonthStrings,
  getStringDate,
  dateToUTCString,
} from '../../../utils/date';
import StatsCard from './Stats/StatsCard';
import PromotionsSection from './Sections/PromotionsSection';
import ClientsSection from './Sections/ClientsSection';
import ProductsSection from './Sections/ProductsSection';
import ProductsLessStockSection from './Sections/ProductsLessStockSection';
import ColaboratorsSalesModal from './Modals/ColaboratorsSalesModal';

import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import TooltipComponent from '../components/Stats/Tooltip';
import CustomDateModal from './Modals/CustomDateModal';
import {capitalize} from '../../../utils/string';
import {getRangeDatesByPeriod} from '../utils';

import type {
  User,
  Currency,
  Store,
  Subscription,
  SubscriptionSetting,
} from '../../../entities';

import type {
  OrderData,
  OrderCustomer,
  ProductCategoryData,
  ProductData,
  SalesData,
  ViewsData,
  MostSeenProductData,
  DashboardAction,
  PromotionSalesData,
  ProductLessStockData,
} from '../duck';
import {getCustomDomain} from '../../../utils/component';
import FreeTrialBanner from '../../../elements/FreeTrialBanner/FreeTrialBanner';
import ConsultancyPageAlert from '../../../elements/ConsultancyPageAlert';
import {TIENDADA_LINKS} from '../../../constants';

type Props = {|
  user?: User,
  store?: Store,
  ordersCount?: string,
  productsCount?: number,
  fetchDataAnalytics?: ({dateIni: string, dateEnd: string}) => void,
  orderData?: OrderData,
  productCategoryData?: ProductCategoryData,
  productData?: ProductData,
  salesData?: SalesData,
  viewsData?: ViewsData,
  mostSeenProducts?: MostSeenProductData[],
  isFetchingData?: boolean,
  error?: string,
  currency?: Currency,
  dateIni?: Date,
  errorAnalyticsFetched?: string,
  fetchDataAnalytics: () => void,
  pageViews?: number,
  isFetchingViewsData?: boolean,
  fetchSummaryProductsCount: () => void,
  fetchSummaryPagesView: () => void,
  fetchPageViews: (payload: DashboardAction) => void,
  fetchOrdersCount: (payload: DashboardAction) => void,
  fetchSummaryProducts: (payload: DashboardAction) => void,
  fetchSummaryOrders: (payload: DashboardAction) => void,
  fetchSummaryProductCategories: (payload: DashboardAction) => void,
  fetchSummarySales: () => void,
  fetchSummaryOrderAmount: (payload: DashboardAction) => void,
  fetchSummaryAverageTicket: (payload: DashboardAction) => void,
  fetchSummaryOrderCustomers: (payload: DashboardAction) => void,
  fetchPromotionSales: (payload: DashboardAction) => void,
  fetchLessStockProducts: (payload: DashboardAction) => void,
  fetchFlagLessStockProducts: () => void,
  orderAmount?: string,
  averageTicket?: string,
  subscription?: Subscription,
  subscriptionSetting?: SubscriptionSetting,
  isFetchingCustomers?: boolean,
  customers?: OrderCustomer[],
  isFetchingLessStockProducts?: boolean,
  lessStockProducts?: ProductLessStockData[],
  isFetchingPromotionsSales?: boolean,
  isFetchingSummaryProducts?: boolean,
  promotionSales?: PromotionSalesData[],
  isFetchingFlaglessProducts?: boolean,
  flagLessStockProducts?: boolean,
  dueInformation?: Subscription,
|};

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 1500,
    // display: 'flex',
    // flexDirection: 'column',
    background: '#f8f9fb',
    paddingBottom: 96,
  },
  greeting: {
    fontSize: '1.5rem',
    margin: '.75rem',
  },
  bold: {
    fontWeight: 600,
  },
  welcome: {
    margin: '.75rem',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  welcomeContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  pageViewCountBanner: {
    margin: '.75rem',
    // width: '100%',
    height: 72,
    borderRadius: '16px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    overflow: 'visible',
    color: 'white',
    background: colors.purple3,
    backgroundImage: `url(${purpleEllipsesPageViewsBackground})`,
    backgroundSize: '100% 100%',
    [theme.breakpoints.up('md')]: {
      backgroundSize: 'auto 100%',
    },
  },
  pageViewCountBannerLeft: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '2rem',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'space-around',
    },
  },
  pageViewsText: {
    fontWeight: 500,
    [theme.breakpoints.only('xs')]: {
      marginTop: '8px',
    },
  },
  pageViews: {
    fontWeight: 600,
    fontSize: '2rem',
    whiteSpace: 'nowrap',
  },
  divider: {
    background: colors.grey300,
  },
  pageViewsBannerDomain: {
    marginLeft: theme.spacing(2),
    fontWeight: 500,
    fontSize: '1.125rem',
  },
  pageViewsBannerShareStoreButton: {
    color: 'white',
    fontSize: '0.875rem',
    '& .MuiButton-label': {
      flexDirection: 'row',
    },
    [theme.breakpoints.only('xs')]: {
      marginTop: '-8px',
    },
  },
  actionButtonInfoIcon: {
    color: 'white',
    marginLeft: 'auto',
  },
  infoTooltip: {
    color: colors.purple3,
    backgroundColor: colors.lightPurple,
    borderRadius: '8px',
    textAlign: 'left',
    maxWidth: 320,
  },
  gridContainer: {
    margin: '.75rem',
    // [theme.breakpoints.only('sm')]: {
    //   padding: theme.spacing(2, 3, 0, 4),
    // },
    // [theme.breakpoints.down('xs')]: {
    //   padding: theme.spacing(2, 1, 0, 2),
    // },
  },
  leftContent: {
    flex: '1 1 30%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: '16px',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 2, 2),
    },
    background: 'white',
  },
  rightContent: {
    flex: '1 1 70%',
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      marginLeft: theme.spacing(4),
    },
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(4),
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    background: colors.purple3,
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  logo: {
    height: 48,
    width: 'auto',
  },
  dashboardItem: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: '15px',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
  },
  dashboardHeader: {
    display: 'flex',
    alignItems: 'center',
    '& img': {
      marginRight: '0.5rem',
    },
  },
  dashboardItemClickable: {
    '&:hover': {
      cursor: 'pointer',
      filter: 'brightness(0.95)',
    },
  },
  dashboardItemTitle: {
    fontWeight: 600,
  },
  emptyChartBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff10',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartBackdropSales: {
    backgroundColor: 'rgba(255, 255, 255, 0.6)!important',
  },
  emptyChartAlert: {
    background: colors.grey200,
    borderRadius: '24px',
    width: 260,
    padding: theme.spacing(3),
    textAlign: 'center',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  chartAlertSales: {
    backgroundColor: '#FFFFFF!important',
    padding: theme.spacing(3, 2),
    boxShadow: '0px 4px 16px rgba(55, 61, 76, 0.16)',
    borderRadius: '12px',
  },
  wraperIcon: {
    backgroundColor: '#F5F0FF',
    borderRadius: '50%',
    padding: theme.spacing(1),
    width: '40px',
    height: '40px',
  },
  icon: {
    color: colors.purple3,
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  amountCard: {
    fontSize: '22px',
    fontWeight: 600,
    color: colors.grey900,
    textAlign: 'left',
  },
  titleText: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.grey900,
    textAlign: 'left',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
  },
  firstSection: {
    height: '100%',
  },
  chartDescription: {
    marginTop: 4,
    fontSize: '0.875rem',
    color: colors.grey600,
  },
  pieChartContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    margin: '0px 0 0px -16px',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  pieChartLegends: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  pieChartLabel: {
    fontWeight: 500,
  },
  product: {
    display: 'flex',
    alignItems: 'center',
    margin: '16px 8px 0 8px',
  },
  productNumber: {
    color: colors.grey600,
    width: 24,
    minWidth: 24,
  },
  productImage: {
    height: 44,
    width: 44,
    objectFit: 'cover',
    borderRadius: '12px',
    marginRight: theme.spacing(2),
  },
  productName: {
    color: colors.grey800,
    fontSize: '0.875rem',
    display: '-webkit-box',
    lineClamp: 2,
    overflow: 'hidden',
    '-webkit-box-orient': 'vertical',
    marginRight: theme.spacing(2),
  },
  productSalesCount: {
    marginLeft: 'auto',
    textAlign: 'center',
    fontSize: '0.75rem',
    '& b': {
      fontSize: '0.875rem',
    },
  },
  containerEarnedPoints: {
    height: '100%',
    backgroundColor: '#F5F0FF',
    borderRadius: '8px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  wrapperImgReferred: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  inviteFriend: {
    // width: '6rem',
    width: '100px',
    margin: '0 auto',
  },
  earnedText: {
    color: '#5E1FDB',
    fontSize: '14px',
    fontWeight: 500,
    [theme.breakpoints.up('md')]: {
      fontSize: '16px',
    },
  },
  smallEarnedText: {
    color: '#5E1FDB',
    fontSize: '12px',
    fontWeight: 500,
  },
  earnedPoints: {
    color: '#5E1FDB',
    fontSize: '24px',
    fontWeight: 600,
  },
  btnKnowReferral: {
    marginTop: '1rem',
    fontWeight: 600,
    backgroundColor: '#FFDF65',
    borderRadius: '32px',
    color: '#500BDB',
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: '#FBBF34!important',
    },
  },
  containerShowPoints: {
    width: '100%',
    background: '#F5F0FF',
    borderRadius: '8px',
    padding: theme.spacing(1),
  },
  showPoints: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  learnPro: {
    width: '100%',
    margin: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  textPro: {
    textAlign: 'center',
    color: '#7B39FF',
    fontSize: '18px',
    fontWeight: 600,
    '& span': {
      backgroundColor: '#7B39FF',
      color: '#FFFFFF',
      padding: '0 5px',
    },
  },
  seeDetails: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 4),
    border: '1px solid #7B39FF',
    fontSize: '14px',
    color: '#7B39FF',
  },
  referralBtn: {
    padding: '0',
    display: 'inline',
  },
  stats: {
    fontWeight: 500,
    color: colors.grey900,
    fontSize: 18,
  },
  singleSection: {
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  textAlertSale: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  salesLineFilter: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: colors.grey300,
  },
  salesBtGroup: {
    justifyContent: 'flex-start',
    // marginBottom: '1rem',
    borderBottom: '1px solid #E8E8ED',
    [theme.breakpoints.down('md')]: {
      // width: '100%',
      display: 'flex',
      alignItems: 'center',
      '& button': {
        width: '100%',
      },
    },
  },
  btnFilterSales: {
    width: 'fit-content!important',
    color: colors.grey900,
    fontWeight: 400,
    borderRadius: 0,
  },
  btnFilterSalesSelected: {
    fontWeight: 600,
    borderBottom: `1px solid ${colors.purple}`,
  },
  datePicker: {
    width: '15rem',
  },
  btnCalendar: {
    border: 'none',
    borderRadius: '0!important',
    color: '#55556D',
    background: 'unset',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '&:hover': {
      background: 'unset',
    },
    '&:focus': {
      background: 'unset',
    },
  },
  sizeSelect: {
    border: 'none!important',
    background: 'unset',

    '& > div': {
      border: 'none!important',
      '&:hover': {
        background: 'unset',
      },
    },
    '&:hover': {
      background: 'unset',
    },
  },
  groupSelect: {
    borderRadius: '25px',
    backgroundColor: '#FFFFFF',
    marginTop: '1rem',
    border: '1px solid #B8B8C7',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      marginLeft: '1rem',
      marginTop: '0',
    },
  },
  containerTutorialButton: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row-reverse',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  viewsRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

export default usePage<Props>(Dashboard, 'Dashboard');

const pieChartColors = ['#7B39FF', '#3DD598', '#FFC442', '#FE974A', '#158CFD'];

const RADIAN = Math.PI / 180;
const renderCustomizedPieLabel = props => {
  const {cx, cy, midAngle, innerRadius, outerRadius, percent} = props;
  const radius = innerRadius + (outerRadius - innerRadius) * 1.25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="black"
      fontWeight={500}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomBarchartTooltip = ({
  active,
  payload,
  label,
  currency,
}: {
  active?: any,
  payload?: any,
  label?: string,
  currency?: any,
}) => {
  if (active && payload && payload.length && currency) {
    return (
      <div
        style={{
          background: '#500BDB',
          color: 'white',
          borderRadius: '8px',
          padding: '2px 8px',
        }}
      >
        <p style={{fontSize: '0.875rem', fontWeight: 500}}>
          {commaFormat(payload[0].value, currency.symbol)}
        </p>
      </div>
    );
  }

  return null;
};

const CustomAreaChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: any,
  payload?: any,
  label?: string,
}) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: '#500BDB',
          color: 'white',
          borderRadius: '8px',
          padding: '2px 8px',
        }}
      >
        <p style={{fontSize: '0.875rem', fontWeight: 500}}>
          {payload[0].value}
        </p>
      </div>
    );
  }

  return null;
};

function Dashboard(props: Props) {
  const {
    user,
    store,
    isFetchingData,
    productCategoryData,
    productData,
    orderData,
    ordersCount,
    currency,
    pageViews,
    fetchPageViews,
    mostSeenProducts,
    isFetchingViewsData,
    productsCount,
    fetchSummaryProductsCount,
    fetchSummaryPagesView,
    fetchSummaryProducts,
    fetchSummaryOrders,
    fetchSummaryProductCategories,
    fetchSummarySales,
    fetchSummaryOrderAmount,
    fetchSummaryAverageTicket,
    fetchSummaryOrderCustomers,
    fetchPromotionSales,
    fetchLessStockProducts,
    fetchOrdersCount,
    salesData,
    viewsData,
    orderAmount,
    averageTicket,
    isFetchingCustomers,
    customers,
    lessStockProducts,
    isFetchingPromotionsSales,
    promotionSales,
    isFetchingSummaryProducts,
    isFetchingFlaglessProducts,
    flagLessStockProducts,
    fetchFlagLessStockProducts,
  } = props;

  const classes = useStyles();
  const history = useHistory();

  const [periodValue, setPeriodValue] = useState('monthly');
  const [dateConfig, setDateConfig] = useState({init_date: '', end_date: ''});

  const [openDateModal, setOpenDateModal] = useState(false);
  const [openColaboratorsModal, setOpenColaboratorsModal] = useState(false);

  const buildCommonQueryParamsCallback = useCallback(() => {
    if (dateConfig && periodValue) {
      let start_date = '';
      let end_date = '';
      if (periodValue !== 'custom') {
        const range = getRangeDatesByPeriod(periodValue);
        start_date = dateToUTCString(range.init_date, {startDay: true});
        end_date = dateToUTCString(range.end_date, {endDay: true});
      } else if (dateConfig.init_date && dateConfig.end_date) {
        start_date = dateToUTCString(dateConfig.init_date, {startDay: true});
        end_date = dateToUTCString(dateConfig.end_date, {endDay: true});
      }

      return {
        start_date,
        end_date,
      };
    }
  }, [dateConfig, periodValue]);

  useEffect(() => {
    if (user) {
      fetchSummaryPagesView();
      fetchSummaryProductsCount();
      fetchSummarySales();
      fetchFlagLessStockProducts();
    }
  }, [
    fetchFlagLessStockProducts,
    fetchSummaryPagesView,
    fetchSummaryProductsCount,
    fetchSummarySales,
    user,
  ]);

  useEffect(() => {
    if (dateConfig && periodValue) {
      const dateRange = buildCommonQueryParamsCallback();
      fetchPageViews({dateRange});
      fetchSummaryProductCategories({dateRange});
      fetchSummaryOrders({dateRange});
      fetchSummaryAverageTicket({dateRange});
      fetchSummaryOrderAmount({
        dateRange,
      });

      const orderStates = [
        'PENDING',
        'CONFIRMED',
        'PAID',
        'ON_THE_WAY',
        'DELIVERED',
      ];
      fetchOrdersCount({dateRange, orderStates});
    }
  }, [
    buildCommonQueryParamsCallback,
    dateConfig,
    fetchOrdersCount,
    fetchPageViews,
    fetchSummaryAverageTicket,
    fetchSummaryOrderAmount,
    fetchSummaryOrders,
    fetchSummaryProductCategories,
    fetchSummarySales,
    periodValue,
  ]);

  const getAbvDateName = (name: string) => {
    const sName = name.split(' ');
    const p1 = sName[0];
    const p2 = sName[2];
    if (p1 && p2) return capitalize(`${p2.slice(0, 3)} ${p1}`);
    return '';
  };

  const getStringRangeDates = useMemo(() => {
    if (dateConfig) {
      let strInitDate;
      let strEndDate;
      if (periodValue !== 'custom') {
        const {init_date, end_date} = getRangeDatesByPeriod(periodValue);
        strInitDate = getStringDate(init_date.getTime());
        strEndDate = getStringDate(end_date.getTime());
      } else if (dateConfig.init_date && dateConfig.end_date) {
        const {init_date, end_date} = dateConfig;
        strInitDate = getStringDate(init_date.getTime());
        strEndDate = getStringDate(end_date.getTime());
      }

      if (strInitDate && strEndDate) {
        const nameInitDate = getAbvDateName(strInitDate);
        const nameEndDate = getAbvDateName(strEndDate);
        return `${nameInitDate} - ${nameEndDate}`;
      }
      return '';
    }
    return '';
  }, [dateConfig, periodValue]);

  const [linkCopied, setLinkCopied] = useState(false);

  const PageViewsShareStoreButton = () => (
    <ClickAwayListener onClickAway={() => setLinkCopied(false)}>
      <Tooltip
        placement="top"
        arrow
        PopperProps={{
          disablePortal: true,
        }}
        onClose={() => setLinkCopied(false)}
        open={linkCopied}
        disableHoverListener
        disableTouchListener
        leaveDelay={300}
        title="¡Link copiado!"
      >
        <div>
          <CustomButton
            className={classes.pageViewsBannerShareStoreButton}
            startIcon={<Share />}
            onClick={() => {
              !!store &&
                navigator &&
                navigator.clipboard &&
                navigator.clipboard
                  .writeText(`https://${getCustomDomain(store)}`)
                  .then(() => setLinkCopied(true))
                  .catch(e => console.error(e));
            }}
          >
            Compartir Tienda
          </CustomButton>
        </div>
      </Tooltip>
    </ClickAwayListener>
  );

  const mobile = useMediaQuery('(max-width:600px)');

  const [emptySalesData, setEmptySalesData] = useState(true);
  const [emptyViewsData, setEmptyViewsData] = useState(true);

  const processedSalesData = useMemo(() => {
    const data = [];
    const currentMonth = dayjs().month();
    let allZero = true;
    abvMonthStrings.forEach((m, i) => {
      if (!mobile || i > 5) {
        if (salesData && salesData[i] && parseFloat(salesData[i].amount) > 0)
          allZero = false;
        data.push({
          month: abvMonthStrings[(i + 1 + currentMonth) % 12],
          amount:
            salesData && salesData[i] ? parseFloat(salesData[i].amount) : 0,
        });
      }
    });
    if (allZero) {
      setEmptySalesData(true);
    } else setEmptySalesData(false);
    return data;
  }, [mobile, salesData]);

  const processedViewsData = useMemo(() => {
    const data = [];
    const currentMonth = dayjs().month();
    let allZero = true;
    if (viewsData) {
      const intervals = viewsData.length;
      abvMonthStrings.forEach((m, i) => {
        if (viewsData[i] && parseFloat(viewsData[i].views) > 0) allZero = false;
        if (i < intervals) {
          if (!mobile || i > 5) {
            const diff =
              currentMonth > intervals
                ? currentMonth - intervals
                : currentMonth;
            const idx = (i + 1 + diff) % 12;
            data.push({
              month: abvMonthStrings[idx],
              views: viewsData[i] ? parseFloat(viewsData[i].views) : 0,
            });
          }
        }
      });
    }
    if (allZero) {
      setEmptyViewsData(true);
    } else setEmptyViewsData(false);
    return data;
  }, [mobile, viewsData]);

  const salesPerCategory = useMemo(() => {
    if (productCategoryData && productCategoryData.salesPerCategory) {
      return productCategoryData.salesPerCategory.map(s => {
        return {
          name: s.name,
          amount: parseFloat(s.amount),
        };
      });
    }
  }, [productCategoryData]);

  const salesGroupCategories = useMemo(() => {
    if (salesPerCategory) {
      if (salesPerCategory.length > 5) {
        let amount = 0;
        salesPerCategory
          .slice(4, -1)
          .forEach(i => (amount = amount + i.amount));

        return [
          ...salesPerCategory.slice(0, 4),
          {
            name: 'Otros',
            amount,
          },
        ];
      } else return salesPerCategory;
    }
  }, [salesPerCategory]);

  const pieChart = salesGroupCategories && (
    <div className={classes.pieChartContainer}>
      <PieChart width={240} height={190} margin={{top: 16, bottom: 16}}>
        <Pie
          data={
            salesGroupCategories && salesGroupCategories.length
              ? salesGroupCategories
              : [{name: 'Categoría', amount: 1}]
          }
          dataKey="amount"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={75}
          fill="#82ca9d"
          label={renderCustomizedPieLabel}
          labelLine={false}
          animationBegin={0}
          animationDuration={1000}
          isAnimationActive={false}
        >
          {(salesGroupCategories && salesGroupCategories.length
            ? salesGroupCategories
            : [{name: 'Categoría', amount: 1}]
          ).map((entry, i) => (
            <Cell
              key={`cell-${i}`}
              fill={pieChartColors[i % pieChartColors.length]}
            />
          ))}
        </Pie>
        <RechartsTooltip
          contentStyle={{
            background: '#9595AB',
            border: 'none',
            padding: '0 8px',
            borderRadius: '8px',
          }}
          itemStyle={{
            fontSize: 12,
            color: 'white',
            fontWeight: 500,
          }}
        />
      </PieChart>
      <Hidden mdUp>
        <div className={classes.pieChartLegends}>
          {salesGroupCategories.map((c, i) => (
            <div
              key={`pie-legend-${i}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '4px 0',
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: pieChartColors[i],
                  marginRight: 8,
                }}
              />
              <Typography style={{fontSize: '0.75rem'}}>
                {c.name || '--'}
              </Typography>
            </div>
          ))}
        </div>
      </Hidden>
    </div>
  );

  const withoutPromotionSales = promotionSales && !promotionSales.length;
  const withoutLessStockProducts = !flagLessStockProducts;
  const withoutCustomers = customers && !customers.length;
  const withoutProducts = !(
    (productData && productData.length) ||
    (mostSeenProducts && mostSeenProducts.length)
  );
  const disabledBottomSection =
    withoutPromotionSales &&
    withoutProducts &&
    withoutLessStockProducts &&
    withoutCustomers;

  if (!user || isFetchingData) return null;

  return (
    <>
      <AlertDueDateTier />
      <div className={classes.root}>
        <ConsultancyPageAlert />
        <Typography className={classes.greeting}>
          Hola <span className={classes.bold}>{user && user.firstName}!</span>
        </Typography>
        <div className={classes.welcome}>
          <div className={classes.welcomeContent}>
            <Typography>Te mostramos el resumen de ventas de: </Typography>
            <div className={classes.groupSelect}>
              <CustomSelect
                pagination
                disableUnderline
                className={classes.sizeSelect}
                value={periodValue}
                onChange={e => setPeriodValue(e.target.value)}
              >
                <MenuItem value="today">Hoy</MenuItem>
                <MenuItem value="weekly">Esta semana</MenuItem>
                <MenuItem value="monthly">Este mes</MenuItem>
                <MenuItem value="threeMonths">Últimos 3 meses</MenuItem>
                <MenuItem value="sixMonths">Últimos 6 meses</MenuItem>
                <MenuItem value="twelveMonths">Últimos 12 meses</MenuItem>
                {periodValue === 'custom' && (
                  <MenuItem value="custom" disabled>
                    Custom
                  </MenuItem>
                )}
              </CustomSelect>
              <div style={{borderLeft: '1px solid #B8B8C7'}}>
                <CustomButton
                  onClick={() => setOpenDateModal(true)}
                  className={classes.btnCalendar}
                  startIcon={<DateRangeIcon />}
                >
                  {getStringRangeDates}
                </CustomButton>
              </div>
            </div>
          </div>
          <div className={classes.containerTutorialButton}>
            <CustomButton
              href={TIENDADA_LINKS.youtube.main_channel}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.tutorialsButton}
              startIcon={<PlayCircleFilledTwoTone />}
            >
              Tutoriales
            </CustomButton>
          </div>
        </div>
        <FreeTrialBanner />
        <div className={classes.pageViewCountBanner}>
          <div className={classes.pageViewCountBannerLeft}>
            <img src={whiteGlobe} alt="" />
            <div style={{textAlign: 'center'}}>
              <Typography className={classes.pageViewsText}>
                Visitas a tu web
              </Typography>
              <Hidden smUp>
                <PageViewsShareStoreButton />
              </Hidden>
            </div>
            <Typography className={classes.pageViews} id="store-visits">
              &nbsp;{pageViews}&nbsp;
              <Tooltip
                classes={{tooltip: classes.infoTooltip}}
                placement="right"
                title="Total histórico de visitas únicas. Puede diferir de la suma del gráfico porque un mismo visitante puede aparecer en varios períodos, pero en el total solo se cuenta una vez. La información puede tardar unos minutos en actualizar."
              >
                <InfoOutlined
                  color="primary"
                  className={classes.actionButtonInfoIcon}
                />
              </Tooltip>
            </Typography>
          </div>
          <Hidden mdDown>
            <div style={{width: '100%', display: 'flex', alignItems: 'center'}}>
              <Divider
                orientation="vertical"
                className={classes.divider}
                style={{height: '3rem', marginRight: '1rem'}}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography>
                  URL de tienda:
                  <span className={classes.pageViewsBannerDomain}>
                    {store && getCustomDomain(store)}
                  </span>
                </Typography>
                <Hidden xsDown>
                  <PageViewsShareStoreButton />
                </Hidden>
              </div>
            </div>
          </Hidden>
        </div>
        <div className={classes.gridContainer}>
          <Grid container spacing={3}>
            {/* <Grid item xs={12}>
              <PendingConfigurations size={3} />
            </Grid> */}
            <Grid item xs={12}>
              <div className={classes.dashboardItem}>
                <div className={classes.viewsRow}>
                  <Typography className={classes.dashboardItemTitle}>
                    Visitas a tu web
                  </Typography>
                  <div style={{marginLeft: 8}}>
                    <TooltipComponent
                      placement="left"
                      title="Incluye las visitas a todas las páginas de tu tienda. La suma de las barras del gráfico puede no coincidir con el total histórico: un mismo visitante puede aparecer en varios períodos, mientras que el total cuenta usuarios únicos solo una vez."
                    />
                  </div>
                </div>
                {processedViewsData && (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart
                        width={730}
                        height={250}
                        data={processedViewsData}
                        margin={{
                          top: 30,
                          right: 30,
                          left: 0,
                          bottom: 0,
                        }}
                      >
                        <CartesianGrid strokeDasharray="8 8" vertical={false} />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          height={40}
                          tickMargin={16}
                          tick={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#B8B8C7',
                          }}
                        />
                        <RechartsTooltip content={<CustomAreaChartTooltip />} />
                        <YAxis axisLine={false} tickLine={false} />
                        <Area dataKey="views" stroke="#8884d8" fill="#8884d8" />
                        <Tooltip />
                      </AreaChart>
                    </ResponsiveContainer>
                  </>
                )}
                {emptyViewsData && (
                  <div className={classes.emptyChartBackdrop}>
                    <div
                      className={classes.emptyChartAlert}
                      style={{
                        background: isFetchingViewsData
                          ? 'transparent'
                          : undefined,
                      }}
                    >
                      {!isFetchingViewsData ? (
                        'Necesitas tener mas visitas para poder generar un gráfico de progreso'
                      ) : (
                        <CircularProgress />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    {orderData &&
                      ORDER_STATES.map((state, index: number) => {
                        if (!['CANCELLED'].includes(state)) {
                          const item = orderData.find(
                            o => o.orderState === state
                          );
                          if (item)
                            return (
                              <Grid
                                key={`order-dashboard-${index}`}
                                item
                                xs={12}
                                lg={2}
                              >
                                {currency && (
                                  <OrderCard
                                    quantity={item.orders}
                                    totalAmount={item.totalAmount}
                                    label={item.orderState}
                                    currencySymbol={currency.symbol}
                                  />
                                )}
                              </Grid>
                            );
                          else return null;
                        } else return null;
                      })}
                    <Grid item xs={12} lg={2}>
                      <Grid container spacing={3}>
                        <Grid style={{height: '100%'}} item xs={6} lg={12}>
                          <div
                            className={`${classes.dashboardItem} ${classes.dashboardItemClickable}`}
                            onClick={() => history.push('/products')}
                          >
                            <div className={classes.cardContent}>
                              <div className={classes.col}>
                                <Typography className={classes.titleText}>
                                  Productos
                                </Typography>
                                <Typography
                                  className={classes.amountCard}
                                  id="total-products"
                                >
                                  {productsCount || 0}
                                </Typography>
                              </div>
                              <div className={classes.wraperIcon}>
                                <LocalMallOutlinedIcon
                                  className={classes.icon}
                                />
                              </div>
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={6} lg={12}>
                          <div
                            className={`${classes.dashboardItem} ${classes.dashboardItemClickable}`}
                            onClick={() =>
                              history.push(`/categories?dashboard=1`)
                            }
                          >
                            <div className={classes.cardContent}>
                              <div className={classes.col}>
                                <Typography className={classes.titleText}>
                                  Categorias
                                </Typography>
                                <Typography
                                  className={classes.amountCard}
                                  id="total-categories"
                                >
                                  {productCategoryData &&
                                  productCategoryData.categoriesCount
                                    ? productCategoryData.categoriesCount
                                    : 0}
                                </Typography>
                              </div>
                              <div className={classes.wraperIcon}>
                                <DashboardOutlinedIcon
                                  className={classes.icon}
                                />
                              </div>
                            </div>
                          </div>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                  <div className={classes.dashboardItem}>
                    <div className={classes.salesLineFilter}>
                      <div className={classes.salesBtGroup}>
                        <CustomButton
                          className={`${classes.btnFilterSales} ${classes.btnFilterSalesSelected}`}
                        >
                          Ventas mensuales
                        </CustomButton>
                      </div>
                      <TooltipComponent
                        placement="left"
                        title="Incluye pedidos a partir del estado PAGADO en adelante."
                      />
                    </div>
                    {processedSalesData && (
                      <>
                        <Hidden xsDown>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              // width={500}
                              // height={300}
                              data={processedSalesData}
                              margin={{
                                top: 16,
                                right: 24,
                                left: 24,
                                bottom: 0,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="8 8"
                                vertical={false}
                              />
                              <XAxis
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                height={40}
                                tickMargin={16}
                                tick={{
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: '#B8B8C7',
                                }}
                              />
                              <YAxis tickCount={7} dataKey="amount" hide />
                              <RechartsTooltip
                                content={
                                  <CustomBarchartTooltip currency={currency} />
                                }
                              />
                              <Bar
                                dataKey="amount"
                                fill="#7B39FF"
                                barSize={16}
                                radius={8}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Hidden>
                        <Hidden smUp>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              layout="vertical"
                              // width={500}
                              // height={300}
                              data={processedSalesData}
                              margin={{
                                top: 0,
                                // right: 88,
                                right: 0,
                                left: -8,
                                bottom: 0,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="8 8"
                                vertical={false}
                                horizontal={false}
                              />
                              <XAxis dataKey="amount" type="number" hide />
                              <YAxis
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                type="category"
                                tickMargin={8}
                                tick={{
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: '#B8B8C7',
                                }}
                              />
                              <RechartsTooltip
                                content={
                                  <CustomBarchartTooltip currency={currency} />
                                }
                              />
                              <Bar
                                dataKey="amount"
                                fill="#7B39FF"
                                barSize={16}
                                radius={8}
                              ></Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Hidden>

                        {emptySalesData && (
                          <div className={classes.emptyChartBackdrop}>
                            <div className={classes.emptyChartAlert}>
                              Necesitas mas ventas para poder generar un gráfico
                              de progreso
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <div className={classes.dashboardItem}>
                    <Typography
                      className={classes.dashboardItemTitle}
                      id="category-sales"
                    >
                      Ventas por categoría
                    </Typography>
                    {salesGroupCategories && salesGroupCategories.length ? (
                      <>
                        <Typography className={classes.chartDescription}>
                          {`${salesGroupCategories.length ||
                            ''} categorías mas vendidas`}
                        </Typography>
                        {pieChart}
                      </>
                    ) : (
                      <>
                        {pieChart}
                        <div className={classes.emptyChartBackdrop}>
                          <div className={classes.emptyChartAlert}>
                            Todavía no tiene las ventas suficientes para generar
                            este gráfico
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <StatsCard
                    title="Ventas Totales"
                    icon={stats1}
                    tooltip="Incluyen TODOS los estados a excepción de cancelado."
                    content={
                      <div className={classes.statsContent}>
                        <Typography className={classes.stats}>
                          {commaFormat(
                            orderAmount ? orderAmount : 0,
                            currency ? currency.symbol : ''
                          )}
                        </Typography>
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatsCard
                    title="Cantidad de Pedidos"
                    icon={stats2}
                    tooltip="Incluyen TODOS los estados a excepción de cancelado."
                    content={
                      <div className={classes.statsContent}>
                        <Typography className={classes.stats}>
                          {ordersCount ? ordersCount : 0}
                        </Typography>
                      </div>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatsCard
                    title="Ticket Promedio"
                    icon={stats3}
                    tooltip="Incluyen TODOS los estados a excepción de cancelado."
                    content={
                      <div className={classes.statsContent}>
                        <Typography className={classes.stats}>
                          {commaFormat(
                            averageTicket ? averageTicket : 0,
                            currency ? currency.symbol : ''
                          )}
                        </Typography>
                      </div>
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.singleSection}>
                <Grid container spacing={3}>
                  {/*   <Grid item xs={12} md={6}>
                    <div className={classes.singleSection}>
                      <ColaboratorsSection setOpen={setOpenColaboratorsModal} />
                      {disabledColaboratorsSale && !disabledBottomSection && (
                        <div
                          className={`${classes.emptyChartBackdrop} ${classes.chartBackdropSales}`}
                        >
                          <div
                            className={`${classes.emptyChartAlert} ${classes.chartAlertSales}`}
                          >
                            <Typography className={classes.textAlertSale}>
                              ¡Potencia tus ventas añadiendo colaboradores a tu
                              tienda!
                            </Typography>
                            <CustomButton variant="contained">
                              Más información
                            </CustomButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </Grid> */}
                  <Grid item xs={12} md={6}>
                    <div className={classes.singleSection}>
                      <PromotionsSection
                        canShowDisabled={!disabledBottomSection}
                        currency={currency}
                        isFetchingPromotionsSales={isFetchingPromotionsSales}
                        promotionSales={promotionSales}
                        fetchPromotionSales={fetchPromotionSales}
                        buildCommonQueryParamsCallback={
                          buildCommonQueryParamsCallback
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <div className={classes.singleSection}>
                      <ProductsSection
                        canShowDisabled={!disabledBottomSection}
                        mostSeenProducts={mostSeenProducts}
                        products={productData}
                        fetchSummaryProducts={fetchSummaryProducts}
                        buildCommonQueryParamsCallback={
                          buildCommonQueryParamsCallback
                        }
                        isFetchingSummaryProducts={isFetchingSummaryProducts}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <div className={classes.singleSection}>
                      <ProductsLessStockSection
                        lessStockProducts={lessStockProducts}
                        fetchLessStockProducts={fetchLessStockProducts}
                        isFetchingFlaglessProducts={isFetchingFlaglessProducts}
                        flagLessStockProducts={flagLessStockProducts}
                      />
                      {withoutLessStockProducts && !disabledBottomSection && (
                        <div
                          className={`${classes.emptyChartBackdrop} ${classes.chartBackdropSales}`}
                        >
                          <div
                            className={`${classes.emptyChartAlert} ${classes.chartAlertSales}`}
                          >
                            <Typography className={classes.textAlertSale}>
                              No pierdas control de tus ventas y registra tus
                              stocks
                            </Typography>
                            <CustomButton to="/products" variant="contained">
                              Registrar stock
                            </CustomButton>
                          </div>
                        </div>
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.singleSection}>
                      <ClientsSection
                        customers={customers}
                        isFetchingCustomers={isFetchingCustomers}
                        currency={currency}
                        buildCommonQueryParamsCallback={
                          buildCommonQueryParamsCallback
                        }
                        fetchSummaryOrderCustomers={fetchSummaryOrderCustomers}
                      />
                      {withoutCustomers && !disabledBottomSection ? (
                        <div className={classes.emptyChartBackdrop}>
                          <div className={classes.emptyChartAlert}>
                            Todavía no tiene las ventas suficientes para generar
                            este gráfico
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </Grid>
                </Grid>
                {disabledBottomSection && (
                  <div
                    className={`${classes.emptyChartBackdrop} ${classes.chartBackdropSales}`}
                  >
                    <div
                      className={`${classes.emptyChartAlert} ${classes.chartAlertSales}`}
                    >
                      <Typography className={classes.textAlertSale}>
                        Necesitas mas información para poder generar un gráfico
                        de progreso
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <CustomDateModal
        open={openDateModal}
        setOpen={setOpenDateModal}
        setDateConfig={setDateConfig}
        setPeriodValue={setPeriodValue}
      />
      <ColaboratorsSalesModal
        open={openColaboratorsModal}
        setOpen={setOpenColaboratorsModal}
      />
    </>
  );
}

Dashboard.propTypes = {
  user: PropTypes.object,
  store: PropTypes.object,
  orderData: PropTypes.array,
  ordersCount: PropTypes.number,
  productCategoryData: PropTypes.object,
  productData: PropTypes.array,
  salesData: PropTypes.array,
  viewsData: PropTypes.array,
  mostSeenProducts: PropTypes.array,
  isFetchingData: PropTypes.bool,
  error: PropTypes.string,
  dateIni: PropTypes.instanceOf(Date),
  errorAnalyticsFetched: PropTypes.string,
  fetchDataAnalytics: PropTypes.func,
  fetchPageViews: PropTypes.func,
  pageViews: PropTypes.number,
  productsCount: PropTypes.number,
  fetchSummaryProductsCount: PropTypes.func.isRequired,
  fetchSummaryPagesView: PropTypes.func.isRequired,
  fetchSummaryProducts: PropTypes.func,
  fetchSummaryOrderss: PropTypes.func,
  fetchSummaryProductCategories: PropTypes.func,
  fetchSummaryOrderCustomers: PropTypes.func.isRequired,
  fetchSummarySales: PropTypes.func,
  fetchOrdersCount: PropTypes.func.isRequired,
  fetchPromotionSales: PropTypes.func.isRequired,
  fetchLessStockProducts: PropTypes.func.isRequired,
  fetchFlagLessStockProducts: PropTypes.func.isRequired,
  subscription: PropTypes.object,
  subscriptionSetting: PropTypes.object,
  orderAmount: PropTypes.string,
  averageTicket: PropTypes.string,
  isFetchingCustomers: PropTypes.bool,
  customers: PropTypes.array,
  isFetchingLessStockProducts: PropTypes.bool,
  lessStockProducts: PropTypes.array,
  isFetchingPromotionsSales: PropTypes.bool,
  isFetchingViewsData: PropTypes.bool,
  isFetchingSummaryProducts: PropTypes.bool,
  promotionSales: PropTypes.array,
  isFetchingFlaglessProducts: PropTypes.bool,
  flagLessStockProducts: PropTypes.bool,
};
